// REACT & Libraries
import { useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Save } from "react-feather";
import Swal from "sweetalert2";
import {
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import { Btn, H5, P } from "../../../../../../AbstractElements";

// Services
import {
  VIEW_BARANGAY_QUERY_KEY,
  BARANGAY_QUERY_KEY,
  barangayService,
} from "../../../../../../Services/barangayService";

import {
  CITY_QUERY_KEY,
  cityService,
  VIEW_CITY_QUERY_KEY,
} from "../../../../../../Services/cityService";

import {
  VIEW_PROVINCE_QUERY_KEY,
  PROVINCE_QUERY_KEY,
  provinceService,
} from "../../../../../../Services/provinceService";

import {
  VIEW_COUNTRY_QUERY_KEY,
  countryService,
} from "../../../../../../Services/countryService";

// Types
import { Country } from "../../../../../../Types/MasterData/Country";
import { Province } from "../../../../../../Types/MasterData/Province";
import { City } from "../../../../../../Types/MasterData/City";

// Initial State
const INITIAL_STATE = {
  code: "",
  city_id: 0,
  name: "",
  status: 1,
};

const CreateBarangayForm = ({ toggle }: { toggle?: () => void }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [provinceCode, setProvinceCode] = useState<string | null>(null);
  const [cityCode, setCityCode] = useState<string | null>(null);

  // Get Countries
  const { data: countryData, isLoading: countryIsLoading } = useQuery<{
    data: Country[];
  }>({
    queryKey: VIEW_COUNTRY_QUERY_KEY,
    queryFn: countryService.getAllCountry,
  });

  const countries: Country[] =
    countryData?.data?.filter((item) => item.status === 1) ?? [];

  // Get Province
  const {
    data: provinceData,
    isLoading: isProvinceLoading,
    isError: isProvinceError,
  } = useQuery<{ data: Province[] }>({
    queryKey: [PROVINCE_QUERY_KEY, countryCode],
    queryFn: () => provinceService.getProvinceByCountry(countryCode as string),
    enabled: !!countryCode,
  });

  const provinces: Province[] =
    provinceData?.data?.filter((item) => item.status === 1) ?? [];

  // Get City
  const {
    data: cityData,
    isLoading: isCityLoading,
    isError: isCityError,
  } = useQuery<{ data: City[] }>({
    queryKey: [CITY_QUERY_KEY, provinceCode],
    queryFn: () => cityService.getCityByProvince(provinceCode as string),
    enabled: !!provinceCode,
  });

  const cities: City[] =
    cityData?.data?.filter((item) => item.status === 1) ?? [];

  const handleProvinceSelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      province: "",
      city: "",
    }));
    setCountryCode(e.target.value);
    setProvinceCode("");
  };

  const handleCitySelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      province: "",
      city: "",
    }));
    setProvinceCode(e.target.value);
  };

  const createBarangayMutation = useMutation({
    mutationFn: barangayService.createBarangay,
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: VIEW_BARANGAY_QUERY_KEY,
      });
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "success",
        title: "Transaction Successful",
        text: result.message,
        confirmButtonColor: "#4e73df",
      });
      setFormData(INITIAL_STATE);
      if (toggle) {
        toggle();
      }
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message ||
        "Failed to create the barangay record. Please try again.";
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "error",
        title: "Transaction Failed",
        text: message,
        confirmButtonColor: "#4e73df",
      });
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    Swal.fire({
      title: "Create Barangay Record?",
      text: "Are you sure you want to create this barangay record?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: '<i class="fa fa-save"></i> Create New Record',
      cancelButtonText: '<i class="fa fa-times"></i> Cancel',
      confirmButtonColor: "#4e73df",
      cancelButtonColor: "#dc3545",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      Swal.fire({
        title: "Creating Barangayy Record...",
        text: "Please wait while the record is being created.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      createBarangayMutation.mutate(formData);
    });
  };

  return (
    <ModalBody className="modal-body">
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <div className="d-flex justify-content-between align-items-center">
          <H5> </H5>
          <P className="mb-2">
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>{" "}
            Fields marked with <b className="text-danger">*</b> are required{" "}
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>
          </P>
        </div>
        <Row className="g-3">
          <div className="p-3 card-wrapper border rounded-3 h-100">
            <Row className="p-1">
              <Col md="12">
                <FormGroup>
                  <Label>
                    Country <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-flag txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="select"
                      id="country"
                      name="country"
                      disabled={countryIsLoading ? true : false}
                      required
                      onChange={(e) => {
                        handleProvinceSelect(e);
                      }}
                    >
                      <option value="">
                        {countryIsLoading ? "Loading..." : "Select Country"}
                      </option>
                      {!countryIsLoading &&
                        countries.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.name}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>
                    Province <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-location-pin txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="select"
                      id="province"
                      name="province"
                      required
                      onChange={(e) => {
                        handleCitySelect(e);
                      }}
                    >
                      <option value="" disabled selected>
                        {isProvinceLoading ? "Loading..." : "Select Province"}
                      </option>
                      {!isProvinceLoading &&
                        provinces.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>
                    City <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-location-pin txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="select"
                      id="city_id"
                      name="city_id"
                      required
                      onChange={handleInputChange}
                    >
                      <option value="" disabled selected>
                        {isCityLoading ? "Loading..." : "Select City"}
                      </option>
                      {!isCityLoading &&
                        cities.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>
                    Barangay <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-info-circle txt-primary"></i>
                    </InputGroupText>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      minLength={2}
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>
                    Status <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-tag txt-primary"></i>
                    </InputGroupText>

                    <Input
                      className="select-scroll"
                      value={formData.status}
                      id="status"
                      name="status"
                      type="select"
                      required
                      onChange={(e) => {
                        handleInputChange(e);
                      }}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>
            </Row>
          </div>

          <div className="d-flex justify-content-end">
            <Col md="6">
              <Btn
                color="primary"
                className="w-100"
                type="submit"
                disabled={createBarangayMutation.isPending}
              >
                <Save size={16} className="me-1" />
                {createBarangayMutation.isPending ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Creating...
                  </>
                ) : (
                  "Create"
                )}
              </Btn>
            </Col>
          </div>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default CreateBarangayForm;
